import { SaveOutlined } from '@ant-design/icons';
import { Alert, Button, Form, Input, Modal, Steps } from 'antd';
import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import InputTag from '../../components/InputTag';
import LocationSearchLocationIQ from '../../components/LocationSearchLocationIQ';
import * as SERVICE from '../../constants/service.types';
import regex from '../../constants/system.keys';
import { addExternalDeviceReturn, checkExternalDeviceReturn, socketEmitEvent, } from '../../redux/actions';

const { Step } = Steps;

export const AddAndEditRemoteSpeaker = ({
  isOpen,
  onClose,
  handleAddSuccessful,
  onUpdate,
  gatewaySerial,
  speaker,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [form] = Form.useForm();
  const [step, setStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoad, setIsLoad] = useState(false);
  const [name, setName] = useState('');
  const [serial, setSerial] = useState('');
  const [requestId, setRequestId] = useState(new Date().getTime());
  const [updateReturn, setUpdateReturn] = useState();
  const [address, setAddress] = useState({
    note: '',
  });

  const [alertUpdate, setAlertUpdate] = useState({
    visible: false,
    type: null,
    message: null,
  });

  const { namespace, addExternalDevice, updateDevice, checkExternalDevice } =
    useSelector((state) => state);

  const onChangeStep = (step) => {
    form.resetFields();
    setStep((state) => (state > step ? step : state));
  };

  const onCheckExternalDevice = useCallback(() => {
    const fSerial = `speaker${new Date().getTime()}`;
    let data = {
      serial: fSerial,
      namespace,
      deviceType: 'speaker_sip',
      extraInfo: {
        factorySerial: fSerial,
      },
    };

    let dataObj = {
      appServiceType: SERVICE.CHECK_EXTERNAL_DEVICE,
      requestId: new Date().getTime(),
      body: JSON.stringify(data),
    };
    return dispatch(socketEmitEvent(dataObj));
  }, [namespace, dispatch]);

  const createNewSpeaker = (nameSpeaker) => {
    if (serial && namespace) {
      const data = {
        namespace,
        deviceType: 'speaker_sip',
        serial: serial,
        extraInfo: {
          name: nameSpeaker.trim(),
          gatewaySerial: gatewaySerial,
        },
      };

      let dataObj = {
        appServiceType: SERVICE.REQUEST_ADD_EXTERNAL_DEVICE,
        requestId: new Date().getTime(),
        body: JSON.stringify(data),
      };
      return dispatch(socketEmitEvent(dataObj));
    }
  };

  const handleOnSubmit = (values) => {
    setIsLoading(true);
    if (step === 0) {
      setName(values.name);
      //  Add new
      if (!speaker) {
        return createNewSpeaker(values.name);
      } else {
        setStep(1);
        form.setFieldsValue({
          nameDefault: values.name,
        });
      }
    }
  };

  const handleAddressChange = (address) => {
    form.setFieldsValue({
      location: address,
    });
    setAddress((state) => ({
      ...state,
      note: address,
    }));
  };

  const handleSelect = (address) => {
    form.setFieldsValue({
      location: address?.note ?? '',
    });
    setAddress(address);
  };

  useEffect(() => {
    // Request create serial for add new
    if (isOpen && !serial && !speaker) {
      onCheckExternalDevice();
    }
  }, [onCheckExternalDevice, isOpen, serial, speaker]);

  useEffect(() => {
    if (checkExternalDevice && checkExternalDevice.result !== 1) {
      setIsLoading(true);
      form.setFields([
        {
          name: 'name',
          errors: [checkExternalDevice.message],
        },
      ]);
    }
    if (checkExternalDevice && checkExternalDevice.result === 1) {
      setIsLoading(false);
      if (
        checkExternalDevice.data &&
        JSON.parse(checkExternalDevice.data)?.serial
      ) {
        setSerial(JSON.parse(checkExternalDevice.data)?.serial);
        dispatch(checkExternalDeviceReturn(null));
        form.resetFields();
      } else {
        form.setFields([
          {
            name: 'name',
            errors: ['Cannot create speaker at the moment'],
          },
        ]);
      }
    }
  }, [checkExternalDevice, dispatch, form]);

  useEffect(() => {
    if (addExternalDevice) {
      setIsLoading(false);
      dispatch(addExternalDeviceReturn(null));
      form.setFieldsValue({
        nameDefault: name,
      });
      return setStep(1);
    }
  }, [addExternalDevice, dispatch, form, name]);

  useEffect(() => {
    if (updateDevice && updateDevice.requestId === requestId) {
      setUpdateReturn(updateDevice);
    }
  }, [updateDevice, requestId]);

  useEffect(() => {
    if (updateReturn && updateReturn.serial === serial) {
      setIsLoad(false);
      if (updateReturn.isSuccess) {
        setUpdateReturn(null);
        setStep(0);
        form.resetFields();
        handleAddSuccessful();
      } else {
        setAlertUpdate({
          visible: true,
          type: 'warning',
          message: t('Something went wrong, try again!!'),
        });
      }
    }
  }, [form, serial, t, updateReturn, handleAddSuccessful]);

  const resetForm = useCallback(() => {
    setSerial('');
    setName('');
    setIsLoad(false);
    setIsLoading(false);
    setUpdateReturn(null);
    form.resetFields();
  }, [form]);

  useEffect(() => {
    return () => {
      resetForm();
    };
  }, [resetForm]);

  const handleOnUpdate = useCallback(
    (values) => {
      if (address && requestId) {
        const data = {
          name: values.nameDefault,
          note: values.location ?? '',
          serial: serial,
          lat: address.lat ?? 0,
          lng: address.lng ?? 0,
          tags: values.tags?.join(';')?.toLowerCase() || ';',
        };
        const dataTimeRequest = new Date().getTime();
        setRequestId(dataTimeRequest);
        setIsLoad(true);
        return onUpdate(data, dataTimeRequest);
      }
    },
    [address, onUpdate, requestId, serial]
  );

  useEffect(() => {
    if (speaker && isOpen) {
      setName(speaker.name);
      setSerial(speaker.serial);
      setAddress({ note: speaker.note || '' });
      form.setFieldsValue({
        name: speaker.name,
        nameDefault: speaker.name,
        tags: (speaker.tags || '').split(';'),
      });
    }
  }, [speaker, form, isOpen]);

  return (
    <Modal
      title={speaker ? t('Update speaker') : t('Add new speaker')}
      visible={isOpen}
      footer={null}
      onCancel={() => {
        onClose();
        dispatch(checkExternalDeviceReturn(null));
        resetForm();
      }}
    >
      {step === 0 && (
        <Fragment>
          <div>
            <Steps
              size="small"
              current={step}
              onChange={onChangeStep}
              type="navigation"
            >
              <Step title={speaker ? t('Change name') : t('Create')} />
              <Step title={t('Update')} />
            </Steps>
          </div>
          {step === 0 && (
            <div
              className="title-add-box"
              style={{ paddingBottom: '10px', paddingTop: '10px' }}
            >
              {t('Enter information of speaker.')}
            </div>
          )}
          <div style={{ paddingTop: '20px' }}>
            <Form layout="vertical" form={form} onFinish={handleOnSubmit}>
              {step === 0 && (
                <Fragment>
                  <Form.Item
                    label={t('Speaker Name')}
                    name="name"
                    rules={[
                      {
                        required: true,
                        message: t('Please enter the speaker name!'),
                      },
                      {
                        max: 80,
                        message: t('Please input up to 80 characters!'),
                      },
                    ]}
                  >
                    <Input placeholder={t('Enter the speaker name')} />
                  </Form.Item>
                </Fragment>
              )}
              <Form.Item wrapperCol={{ span: 24, offset: 20 }}>
                <Button htmlType="submit" type="primary" loading={isLoading}>
                  {speaker ? t('Next') : t('Create')}
                </Button>
              </Form.Item>
            </Form>
          </div>
        </Fragment>
      )}

      {/* Update speaker */}
      {step === 1 && (
        <Fragment>
          <div>
            <Steps
              size="small"
              current={step}
              onChange={onChangeStep}
              type="navigation"
            >
              <Step title={speaker ? t('Change name') : t('Create')} />
              <Step title={t('Update')} />
            </Steps>
          </div>
          <div style={{ paddingTop: '20px' }}>
            <Form layout="vertical" form={form} onFinish={handleOnUpdate}>
              <Form.Item label={t('Speaker Name')} name="nameDefault">
                <Input disabled placeholder={t('Enter the speaker name')} />
              </Form.Item>

              <Form.Item
                name="location"
                rules={[
                  {
                    pattern: regex.isName,
                    message: t('Please enter only letters and numbers!'),
                  },
                  {
                    max: 200,
                    message: t('Please input up to 200 characters!'),
                  },
                ]}
                label={t('Location')}
              >
                <LocationSearchLocationIQ
                  address={address.note}
                  onChange={handleAddressChange}
                  onAddressSelect={handleSelect}
                />
              </Form.Item>
              <Form.Item
                name="tags"
                className="tags-device"
                label={t('Tag')}
                style={{ borer: '1px solid #434343', borderRadius: '2px' }}
              >
                <InputTag numberTag={3} nameAdd="Add tag" />
              </Form.Item>
              <div className="edit-gateway-alert">
                {alertUpdate.visible && (
                  <Alert
                    {...alertUpdate}
                    showIcon
                    closable
                    onClose={() => setAlertUpdate({ visible: false })}
                  />
                )}
              </div>
              <div className="btn-update-on-add-gateway">
                <Button
                  onClick={() => {
                    resetForm();
                    handleAddSuccessful();
                  }}
                >
                  {t('Skip')}
                </Button>
                <Form.Item shouldUpdate>
                  {() => (
                    <Button
                      icon={<SaveOutlined />}
                      htmlType="submit"
                      type="primary"
                      loading={isLoad}
                      disabled={
                        form
                          .getFieldsError()
                          .filter(({ errors }) => errors.length).length
                      }
                    >
                      {t('Update')}
                    </Button>
                  )}
                </Form.Item>
              </div>
            </Form>
          </div>
        </Fragment>
      )}
    </Modal>
  );
};

export default AddAndEditRemoteSpeaker;
